import React, { useState, useEffect, useCallback } from 'react';
import { Shield, Loader, Check } from 'lucide-react';
import { neonQuery } from '../services/api';

const ROLES = ['Admin', 'IT', 'Manager', 'Procurement', 'Fulfillment', 'Customer Service', 'Marketing', 'HR', 'Accounting'];
const ROLE_COLORS = { Admin: '#00395C', IT: '#7c3aed', Manager: '#0e7490', Procurement: '#6b21a8', Fulfillment: '#0369a1', 'Customer Service': '#15803d', Marketing: '#DC582A', HR: '#b45309', Accounting: '#4338ca' };

const SECTIONS = [
  { id: 'dashboard', label: 'Dashboard', desc: 'Main dashboard with alerts, sales, and VIP data' },
  { id: 'ask', label: 'Ask', desc: 'Ask Claude questions, pull reports, read screenshots and your own email (read-only)' },
  { id: 'seventh-street', label: 'Seventh Street', desc: 'Crisfield waterfront development - roadmap, questions, risks, documents', children: [
    { id: 'seventh-street-brain', label: 'Seventh Street Brain', desc: 'Project brain and tracker' },
  ] },
  { id: 'fulfillment', label: 'Fulfillment', desc: 'Shipping Today, Remaining, Products, Gift Certs' },
  { id: 'orders', label: 'Orders', desc: 'Order Details, Placed, On Hold, Claims, Fraud' },
  { id: 'reports', label: 'Reports', desc: 'Sales, Profit, Shipping, Claims, Insurance' },
  { id: 'profit-margin', label: 'Profit Margin', desc: 'By Order, By Product, Detail Report' },
  { id: 'customers', label: 'Customers', desc: 'Customer Search, VIP Search, VIP Club' },
  { id: 'cnc-fraud', label: 'C&C Fraud Watch', desc: 'Flag Crab & Cruise accounts as fraud; alerts when one buys or re-registers' },
  { id: 'cnc-bus-groups', label: 'C&C Bus Groups', desc: 'Bus group payment plans: schedules, declines, grades, charge-today alerts and customer reminders' },
  { id: 'cr-monthly-report', label: 'Monthly Report', desc: 'Customer Relations end-of-month recap: chargebacks, address corrections, feedback, claims; sent from your Outlook' },
  { id: 'reconciliation', label: 'UPS', desc: 'Shipping Reconciliation, Claims' },
  { id: 'feedback', label: 'Feedback', desc: 'Summary, By Category, Email Follow Up' },
  { id: 'procurement', label: 'Procurement', desc: 'Parent section — or grant individual items below', children: [
    { id: 'procurement-brain', label: 'Procurement Brain', desc: 'Buying roadmap, supplier notes, findings, documents' },
    { id: 'waste-log', label: 'Waste Log', desc: 'Product thrown out — what, how much, what it cost and why' },
    { id: 'crab-cake-planner', label: 'Crab Cake Planner', desc: 'Tubs to make, crab and ingredients to order, cost per pack' },
    { id: 'hcd', label: 'HCD', desc: 'Yield Report, Vendor Summary, Demand Planning' },
    { id: 'inventory', label: 'Product Inventory', desc: 'Summary, Pricing, Cost Mapping, Price History, Price Comparison' },
  ] },
  { id: 'marketing', label: 'Marketing', desc: 'Meta, Google, Email campaigns, Conversions, Studio' },
  { id: 'bigcommerce', label: 'BigCommerce', desc: 'Custom Fields management' },
  { id: 'mailing-list', label: 'Mailing List', desc: 'Email list management' },
  { id: 'comms', label: 'Communications', desc: 'Parent section — or grant individual items below', children: [
    { id: 'contact-center-brain', label: 'Contact Center Brain', desc: 'Customer contact roadmap, answer book, findings, documents' },
    { id: 'unified-inbox', label: 'Unified Inbox', desc: 'SMS, voicemail, email, Meta DMs and comments' },
    { id: 'ringcentral', label: 'RingCentral', desc: 'Phone system and call management' },
  ] },
  { id: 'payroll', label: 'Payroll', desc: 'Employee payroll management' },
  { id: 'time-clock', label: 'Time Clock', desc: 'TCP TimeClock Plus — employees, job codes, hours' },
  { id: 'tasks', label: 'Tasks', desc: 'Project tracker — backlog, roadmap, milestones, subtasks' },
  { id: 'configure-site', label: 'Configure Site', desc: 'Parent section — or grant individual items below', children: [
    { id: 'configure-sync', label: 'Sync & Logs', desc: 'Neon sync controls and sync history' },
    { id: 'configure-users', label: 'Users', desc: 'Admin user management and passkey setup' },
    { id: 'configure-roles', label: 'Role Permissions', desc: 'This page — role access control' },
    { id: 'trouble-tickets', label: 'Trouble Tickets', desc: 'Submit and track trouble tickets' },
    { id: 'checkout-telemetry', label: 'Telemetry', desc: 'Checkout + User (auth) telemetry viewer (Cosmos telemetry_events). Admin/IT/Manager.' },
    { id: 'traffic-analysis', label: 'Traffic Analysis', desc: 'Whole-site conversion funnel (GA4 + checkout telemetry) — visits → add-to-cart → checkout steps → purchase, with drop-off points. Admin/Manager.' },
  ]},
  { id: 'knowledge-base', label: 'Knowledge Base', desc: 'Searchable docs from cpx-claude-ai. Admin/IT see technical content; Managers see operator-friendly content.' },
];

export default function RolePermissions() {
  const [perms, setPerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(null); // "role:section"

  const load = useCallback(async () => {
    try {
      const rows = await neonQuery('role_permissions_load');
      setPerms(rows);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const hasPermission = (role, sectionId) => {
    return perms.some(p => p.role === role && p.section_id === sectionId);
  };

  const toggle = async (role, sectionId) => {
    const key = `${role}:${sectionId}`;
    setToggling(key);
    const granted = !hasPermission(role, sectionId);
    try {
      const rows = await neonQuery('role_permissions_toggle', { role, section_id: sectionId, granted });
      setPerms(rows);
    } catch (e) { console.error(e); }
    finally { setToggling(null); }
  };

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-500)' }}>
        <Loader size={16} className="spin" /> Loading permissions...
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 1200 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
        <Shield size={24} color="var(--navy)" />
        <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Role Permissions</h1>
      </div>
      <p style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 20, marginTop: 4 }}>
        Control which sections each role can access. Changes take effect on next login.
      </p>

      <div style={{ background: '#fff', borderRadius: 8, border: '1px solid var(--gray-200)', overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--gray-200)' }}>
              <th style={{ padding: '12px 14px', textAlign: 'left', fontWeight: 700, color: 'var(--gray-700)', position: 'sticky', left: 0, background: '#fff', minWidth: 160, zIndex: 1 }}>
                Section
              </th>
              {ROLES.map(role => (
                <th key={role} style={{ padding: '10px 8px', textAlign: 'center', fontWeight: 700, minWidth: 90 }}>
                  <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 12, fontSize: 10, fontWeight: 700,
                    background: ROLE_COLORS[role], color: '#fff' }}>
                    {role}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {SECTIONS.flatMap((section, idx) => {
              const rows = [];
              const bg = idx % 2 === 0 ? '#fff' : '#fafbfc';
              const renderRow = (item, isChild) => (
                <tr key={item.id} style={{ borderBottom: '1px solid var(--gray-100)', background: bg }}>
                  <td style={{ padding: isChild ? '6px 14px 6px 32px' : '10px 14px', position: 'sticky', left: 0, background: bg, zIndex: 1 }}>
                    <div style={{ fontWeight: isChild ? 500 : 600, color: isChild ? 'var(--gray-600)' : 'var(--gray-800)', fontSize: isChild ? 11 : undefined }}>
                      {isChild ? '└ ' : ''}{item.label}
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--gray-400)', marginTop: 1, lineHeight: 1.3 }}>{item.desc}</div>
                  </td>
                  {ROLES.map(role => {
                    const granted = hasPermission(role, item.id);
                    const key = `${role}:${item.id}`;
                    const isToggling = toggling === key;
                    return (
                      <td key={role} style={{ padding: isChild ? '4px 8px' : '8px', textAlign: 'center' }}>
                        <button onClick={() => toggle(role, item.id)} disabled={isToggling}
                          style={{
                            width: isChild ? 24 : 28, height: isChild ? 24 : 28, borderRadius: 6, border: 'none', cursor: isToggling ? 'wait' : 'pointer',
                            background: granted ? (ROLE_COLORS[role] || 'var(--green)') : 'var(--gray-100)',
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            transition: 'all 0.15s',
                            opacity: isToggling ? 0.5 : 1,
                          }}>
                          {granted && <Check size={isChild ? 12 : 14} color="#fff" strokeWidth={3} />}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              );
              rows.push(renderRow(section, false));
              if (section.children) {
                section.children.forEach(child => rows.push(renderRow(child, true)));
              }
              return rows;
            })}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 16, fontSize: 11, color: 'var(--gray-400)', lineHeight: 1.6 }}>
        <strong>Note:</strong> "Owner-only" items (To-Do, Cameras) are always restricted to Greg Cain regardless of role.
        Dashboard is visible to all authenticated users even without a role assignment.
      </div>
    </div>
  );
}
